import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { Form, Input, Modal, Text } from '@thefarmersfront/kitchen';

import { FormField } from '~/components/common';

import { usePostOwnerDetail } from '~/hooks/vender';
import { useLoadingStore, useUserInfoStore } from '~/store';

import { DEFAULT_INPUT_STATUS, TOAST_MESSAGE, VENDERS } from '~/constants';

import type { TypeErrorResponseData, TypeInputState } from '~/types/common.types';
import type { TypeOwnerDetail } from '~/types/vender.types';

import styles from './VenderPasswordConfirmModal.module.scss';

type TypeVenderPasswordConfirmModalProps = {
  open: boolean;
  onConfirm: (data: TypeOwnerDetail) => void;
};

/**
 * [화주사 정보 관리 > 화주사 정보] 비밀번호 확인 모달
 */
const VenderPasswordConfirmModal = ({ open, onConfirm }: TypeVenderPasswordConfirmModalProps) => {
  const navigate = useNavigate();
  const { postOwnerDetail } = usePostOwnerDetail();

  const { setIsLoading } = useLoadingStore();
  const { userInfo } = useUserInfoStore();

  const [passwordInputState, setPasswordInputState] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);

  const passwordInputRef = useRef<HTMLInputElement>(null);

  const fetchOwnerDetail = async () => {
    try {
      setIsLoading(true);

      const { data } = await postOwnerDetail({ password: passwordInputRef.current?.value ?? '' });

      onConfirm(data);
    } catch (error) {
      if ((error as TypeErrorResponseData['error'])?.code === VENDERS.WRONG_PASSWORD) {
        setPasswordInputState({
          status: 'error',
          message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetPasswordStatus = () => setPasswordInputState(DEFAULT_INPUT_STATUS);

  const handleCancel = () => {
    navigate(-1);
  };

  const handleConfirm = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    if (passwordInputRef.current?.value === '') {
      setPasswordInputState({
        status: 'error',
        message: '비밀번호를 입력해 주세요.',
      });
    } else {
      fetchOwnerDetail();
    }
  };

  return (
    <Modal
      open={open}
      title="비밀번호 확인"
      cancelText="이전 화면으로"
      onCancel={handleCancel}
      onConfirm={handleConfirm}
    >
      <Form className={styles['modal-contents-container']} onSubmit={handleConfirm}>
        <FormField
          label="아이디"
          contents={<Text size="large">{userInfo.loginId}</Text>}
          className={styles['contents-row']}
          labelClassName={styles['contents-row-label']}
        />
        <FormField
          label="비밀번호"
          contents={
            <Input
              ref={passwordInputRef}
              type="password"
              size="small"
              status={passwordInputState.status}
              placeholder="비밀번호를 입력해 주세요."
              validationMessage={passwordInputState.message}
              onFocus={resetPasswordStatus}
              onChange={resetPasswordStatus}
            />
          }
          className={styles['contents-row']}
          labelClassName={styles['contents-row-label']}
        />
      </Form>
    </Modal>
  );
};

export default VenderPasswordConfirmModal;
